/**
 * AC4's PS half, expressed as the condition AC4 actually states.
 *
 * AC4: "On a project group (program) timeline and on a PS project timeline, where
 * there is no single project to list sets for, the button is not shown."
 *
 * The clause that matters is the last one. PIKA-748 implemented the PS half as
 * `!viewer.psProject`, on the belief that a resolved psProject marks a project
 * service project. It does not: `ViewerType.js:797` resolves psProject by asking
 * project-service for a row keyed on the project, and ordinary projects have one.
 * The clause was therefore false everywhere, and shipped the button, the chip and
 * the drawer all disabled (PIKA-773, PIKA-774).
 *
 * `legacyProjectAsSingleProject` answers the real question. A project service
 * project that maps to exactly one legacy project HAS a single project whose sets
 * can be listed, and the surface is legitimate there. One that fans out to many, or
 * to none, resolves null — and that is the case AC4 excludes.
 *
 * Deliberately NOT keyed on psProject being present, and deliberately permissive
 * when there is no psProject at all: a program timeline resolves none, and the
 * 22 September decision keeps the chip drawn there.
 */

export interface TaskSetSurfaceViewer {
	psProject?: {legacyProjectAsSingleProject?: {id?: string} | null} | null;
}

/**
 * False only where a project service project resolves no single legacy project.
 *
 * The narrow reading is on purpose. A gate that is wrong in the permissive
 * direction shows a button somewhere it need not be; wrong in the restrictive
 * direction it removes the entire feature, which is what happened twice.
 */
export const hasSingleProjectForTaskSets = (viewer?: TaskSetSurfaceViewer | null): boolean => {
	const psProject = viewer?.psProject;

	if (!psProject) {
		return true;
	}

	return !!psProject.legacyProjectAsSingleProject;
};

export interface TaskSetSurfacePageComponent {
	props: {
		isProjectTimeline?: boolean;
		sharedContext?: unknown;
		viewer?: TaskSetSurfaceViewer | null;
	};
}

/**
 * Whether this SURFACE may carry a task-set marker at all, bar or sidebar row.
 *
 * Lives here rather than in task_item so the bar and the sidebar row can share one
 * rule without task_group importing task_item -- task_item already reaches into
 * components/groups, and a gate duplicated in two files is a gate that drifts.
 *
 * 1. NOT a project timeline. SCHEDULING_VIEW.PROJECTS does not mean "the project
 *    timeline": canvas_scheduling.js:393 passes it to the multi-project
 *    Schedule -> Projects canvas too. `isProjectTimeline` separates them.
 * 2. The PUBLIC SHARE view, which `isProjectTimeline` does NOT exclude --
 *    CanvasProjectSchedulingShare.js:78-79 computes it as
 *    `!!companyProjectId || !!groupId`. Without `sharedContext` an externally
 *    shared timeline marks task sets for whoever holds the link.
 * 3. AC4's PS half, via hasSingleProjectForTaskSets above.
 *
 * A PROGRAM timeline is deliberately allowed: it renders the full tree through the
 * same components, so set instances appear there and a run of identically-named
 * ones is just as confusing.
 */
export const mayMarkTaskSetsOnSurface = (pageComponent?: TaskSetSurfacePageComponent | null): boolean => {
	const props = pageComponent?.props;

	if (!props?.isProjectTimeline) {
		return false;
	}

	if (props.sharedContext) {
		return false;
	}

	return hasSingleProjectForTaskSets(props.viewer);
};
